import React, { useState } from 'react';
import { Modal, Upload, UploadFile, Progress } from 'antd';
import type { UploadProps, RcFile } from 'antd/es/upload';
import { ButtonCommon, IconCommon, showAlertNotification } from 'components/common';
import { UploadSimple, XCircle, CheckCircle } from '@phosphor-icons/react';
import { InboxOutlined } from '@ant-design/icons';
import { FileUploadModalProps, UploadStatus } from 'types';
import { ExcelFileIcon } from 'utils/importFile';
import { validateFileSize, formatFileSize } from 'utils/uploadFileUtils';
import { useUploadFileToS3 } from 'hooks/useUploadFileToS3';
const { Dragger } = Upload;

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  title = 'TẢI LÊN FILE CỦA BẠN',
  subtitle = 'Vui lòng kéo và thả file vào đây, hoặc bấm để chọn từ thiết bị.',
  acceptedFileTypes = '.xlsx,.xls,.xlsm,.xlm',
  fileTypesHelpText = 'Hỗ trợ các tệp tin xls, xlsm, xlm, xlsx....',
  multiple = false,
  maxFileSize = 5 * 1024 * 1024,
  confirmText = 'Tải lên',
  positionButton = 'right',
  typeModal = 'import',
  onUpload,
  icon = ExcelFileIcon,
  disabled = false,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [key, setkey] = useState<string[]>([]);
  const { uploadFile } = useUploadFileToS3();

  // Reset state and close modal
  const handleCancel = () => {
    if (uploadStatus === 'uploading') {
      // Optional: Add logic to abort upload
      return; // Prevent closing while uploading
    }
    // resetState();
    onClose();
  };

  // Reset all state variables
  const resetState = () => {
    setFileList([]);
    setUploadStatus('idle');
    setUploadProgress(0);
    setTimeRemaining('');
    setUploadedFiles([]);
  };

  const simulateProgress = () => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      setUploadProgress(Math.min(progress, 95)); // Cap at 95% until complete

      // Calculate remaining time based on progress
      const remainingSeconds = Math.round((100 - progress) / 5);
      setTimeRemaining(`Còn lại ${remainingSeconds} giây`);

      if (progress >= 100) {
        clearInterval(interval);
      }
    }, 100);

    return interval;
  };

  const handleUpload = async () => {
    if (fileList.length === 0) onComplete?.([]);

    setUploadStatus('uploading');
    const progressInterval = simulateProgress();

    try {
      const files = fileList.map((file) => file.originFileObj).filter(Boolean) as File[];

      const uploadResults = await Promise.all(files.map((file) => uploadFile(file)));
      setUploadedFiles(uploadResults.map((res) => ({ name: res.fileUrl }) as unknown as File));
      setkey(uploadResults.map((res) => res.keyFile));
      onUpload?.(uploadResults.map((res) => ({ name: res.fileUrl }) as unknown as File));
      clearInterval(progressInterval);
      setUploadProgress(100);
      setTimeout(() => {
        setUploadStatus('done');
      }, 300); // Short delay to ensure the progress bar animation completes
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('error');
      clearInterval(progressInterval);
    }
  };
  //  handler for when user clicks "Complete"
  const handleComplete = () => {
    try {
      if (onComplete) {
        onComplete(uploadedFiles, key);
      }
      resetState();
      onClose();
    } catch (error) {
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: 'Đã xảy ra lỗi khi hoàn thành quá trình tải lên. Vui lòng thử lại.',
      });
      console.error('Error in handleComplete:', error);
    }
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple,
    fileList,
    accept: acceptedFileTypes,
    beforeUpload: (file: RcFile) => {
      // Check file size
      if (!validateFileSize(file, maxFileSize)) {
        return Upload.LIST_IGNORE; // Don't add to file list
      }
      // Return false to prevent automatic upload
      return false;
    },
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
      setUploadStatus('idle'); // Reset status when files change
    },
    onDrop: () => {
      // Handle drop event if needed
    },
    showUploadList: false, // We'll handle our own file list display
  };

  // Render the file list when files are selected but not yet uploading
  const renderFileList = () => {
    if (fileList.length === 0) return null;

    return fileList.map((file) => {
      const fileName = file.name || 'Unknown file';
      const fileSize = file.size ? formatFileSize(file.size) : 'Unknown size';

      return (
        <div
          key={file.uid}
          className='w-full p-4 bg-white rounded-xl border border-[var(--borderColor)] flex items-center gap-3'
        >
          <div className='flex-shrink-0'>
            <IconCommon size='xl' src={icon} />
          </div>

          <div className='flex-grow'>
            <div className='text-[14px] font-medium text-black'>{fileName}</div>
            <div className='text-[12px] text-[var(--textGray)]'>{fileSize}</div>
          </div>

          {uploadStatus !== 'uploading' && (
            <button
              className='flex-shrink-0'
              onClick={() => {
                setFileList(fileList.filter((item) => item.uid !== file.uid));
              }}
            >
              <XCircle size={20} className='text-[var(--sematicErrorColor)]' />
            </button>
          )}
        </div>
      );
    });
  };

  // Render the upload progress UI during file upload
  const renderUploadProgress = () => {
    if (uploadStatus !== 'uploading') return null;

    const file = fileList[0];
    const fileSize = file?.size ? formatFileSize(file.size) : '10 MB';

    return (
      <div className='w-full flex flex-col gap-2 p-4 bg-white rounded-xl border border-[var(--borderColor)]'>
        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-center gap-3'>
            <IconCommon size='xl' src={icon} />
            <div className='flex-grow'>
              <div className='text-[14px] font-medium text-black'>Đang tải lên...</div>
              <div className='text-[12px] text-[var(--textGray)]'>
                {formatFileSize(file?.size || 0)}/{fileSize} • {timeRemaining}
              </div>
            </div>
          </div>

          <button className='flex-shrink-0' disabled>
            <XCircle size={20} className='text-[var(--textGray50)]' />
          </button>
        </div>

        <div className='flex items-center gap-2'>
          <Progress
            percent={uploadProgress}
            showInfo={false}
            strokeColor='var(--sematicColor)'
            className='flex-grow'
            size='small'
          />
          <span className='text-[12px] text-[var(--textGray)]'>{uploadProgress}%</span>
        </div>
      </div>
    );
  };

  // Render the completed upload UI
  const renderUploadComplete = () => {
    if (uploadStatus !== 'done' || fileList.length === 0) return null;

    const file = fileList[0];
    const fileName = file.name || 'Unknown file';
    const fileSize = file.size ? formatFileSize(file.size) : 'Unknown size';

    return (
      <div className='w-full p-4 bg-white rounded-xl border border-[var(--borderColor)] flex items-center gap-3'>
        <div className='flex-shrink-0'>
          <IconCommon size='xl' src={icon} />
        </div>

        <div className='flex-grow'>
          <div className='text-[14px] font-medium text-black'>{fileName}</div>
          <div className='text-[12px] text-[var(--textGray)]'>{fileSize}</div>
        </div>

        <div className='flex-shrink-0'>
          <CheckCircle size={20} weight='fill' className='text-[var(--sematicSuccessColor)]' />
        </div>
      </div>
    );
  };

  const renderDraggerOrProgress = () => {
    if (uploadStatus === 'uploading') {
      return renderUploadProgress();
    }

    if (uploadStatus === 'done') {
      return renderUploadComplete();
    }

    if (fileList.length > 0) {
      return renderFileList();
    }

    return (
      <Dragger {...uploadProps} className='dragger-file-upload'>
        <p className='ant-upload-drag-icon'>
          <InboxOutlined style={{ color: 'var(--bgGreyColor1)', fontSize: '48px' }} />
        </p>
        <p className='ant-upload-text !mt-2'>Nhấp vào hoặc kéo tệp vào khu vực này để tải lên</p>
        <p className='ant-upload-hint !mt-2'>{fileTypesHelpText}</p>
      </Dragger>
    );
  };

  const renderFooterButtons = () => {
    if (uploadStatus === 'done') {
      return (
        <div className='flex justify-end gap-4 mt-2'>
          <ButtonCommon color='none' radius='rounded' onClick={handleCancel} className='min-h-[40px]'>
            Hủy
          </ButtonCommon>

          <ButtonCommon
            color='primary'
            radius='rounded'
            onClick={handleComplete}
            className='min-h-[40px]'
            icon={<CheckCircle size={20} />}
          >
            Hoàn thành
          </ButtonCommon>
        </div>
      );
    }

    return (
      <div
        className={
          'flex gap-4 mt-2 ' +
          (positionButton === 'right' ? 'justify-end' : positionButton === 'left' ? 'justify-start' : 'justify-center')
        }
      >
        <ButtonCommon
          color='none'
          radius='rounded'
          onClick={handleCancel}
          className='min-h-[40px]'
          disabled={uploadStatus === 'uploading'}
        >
          Hủy
        </ButtonCommon>

        <ButtonCommon
          color='primary'
          radius='rounded'
          onClick={handleUpload}
          disabled={(fileList.length === 0 || uploadStatus === 'uploading') && disabled}
          isDisabled={fileList.length === 0 && disabled}
          icon={typeModal === 'import' && <UploadSimple size={20} />}
          className={`min-h-[40px] `}
        >
          {fileList.length === 0 ? 'Bỏ qua' : confirmText}
        </ButtonCommon>
      </div>
    );
  };

  return (
    <Modal
      open={isOpen}
      // onCancel={handleCancel}
      destroyOnClose={true}
      footer={null}
      closeIcon={false}
      width={792}
      centered
      className='modal-none-padding'
    >
      <div className='p-6 flex flex-col gap-4 bg-white rounded-lg'>
        {/* Header */}
        <div className='flex flex-col'>
          <h2 className='text-[20px] font-bold leading-6 text-[var(--textBlack1E)]'>{title}</h2>
          <p className='text-[14px] text-[var(--textGray)] mt-1' dangerouslySetInnerHTML={{ __html: subtitle }} />{' '}
        </div>

        {/* Upload Area or Progress */}
        {renderDraggerOrProgress()}

        {/* Footer Buttons - Different states for different upload stages */}
        {renderFooterButtons()}
      </div>
    </Modal>
  );
};

export default FileUploadModal;
